import { createApiPath } from '@/shared/api/endpoints';
import { apiClient } from '@/shared/api/http-client';

/**
 * 웹 실적 API (`/web/performance/**`, M-08 · M-09 · M-16 ~ M-18)
 * 필드 이름은 서버 그대로 둔다 (docs/api/performance.md).
 * 년월은 모두 yyyyMM이고 경로의 {month}는 정산월이다. 금액 · 비율은 서버가 계산한 값이다.
 * 정산 비율 = 확정 ÷ 대상 × 100 (소수 넷째 자리 절삭), 대상 0이면 null.
 */

/** 월 카드 상태 — 응답에 없는 달(현재 정산월 이후)은 화면에서 "미도래"로 본다 */
export type MonthlyStatus =
  'UNREGISTERED' | 'PENDING_CONFIRMATION' | 'BEFORE_SETTLEMENT' | 'SETTLED';

/** GET /yearly 월 카드 — 조회 가능한 월만, 오름차순 */
export interface YearlyMonth {
  settlementYearMonth: string;
  estimatedAmountPrescription: number;
  targetAmountPrescription: number;
  estimatedAmountSettlement: number;
  targetAmountSettlement: number;
  confirmedAmountSettlement: number;
  settlementRatio: number | null;
  hasSettlement: boolean;
  monthlyStatusCode: MonthlyStatus;
  monthlyStatus: string;
}

/** GET /yearly — 합계는 월 카드 합, 비율은 연간 확정 합 ÷ 연간 대상 합 */
export interface YearlyPerformance {
  year: number;
  clinicId: number | null;
  estimatedAmountPrescription: number;
  targetAmountSettlement: number;
  confirmedAmountSettlement: number;
  settlementRatio: number | null;
  months: YearlyMonth[];
}

/** GET /monthly/{month}/summary — 데이터가 없는 달도 200 + 0 */
export interface MonthlySummary {
  settlementYearMonth: string;
  /** 생략하면 null(본인 전체) */
  clinicId: number | null;
  /** 승인된 확정 처방 + 검수 중 추정 */
  estimatedAmountPrescription: number;
  targetAmountPrescription: number;
  /** 게시 정산 최종 금액 (상태 무관) */
  estimatedAmountSettlement: number;
  /** 확정 상태 정산 총액 */
  targetAmountSettlement: number;
  confirmedAmountSettlement: number;
  settlementRatio: number | null;
  hasSettlement: boolean;
  /** 검수 중 추정 (표준 수수료율 기준, 확정 후 달라질 수 있음) */
  pendingEstimatedAmountPrescription: number;
  /** 검수 중 이미지 장수 */
  pendingPrescriptionCount: number;
}

/** 서버 공통 페이지 응답 (page는 0부터) */
export interface PageResponse<T> {
  content: T[];
  pageNo: number;
  pageSize: number;
  isLast: boolean;
  totalCount: number;
}

/**
 * GET /monthly/{month}/clinics — 좌측 병의원.
 * 확정 · 검수 중 처방 · 노출 이미지 · 병합/게시 정산 중 하나라도 있으면 나온다.
 * 정렬: 예상 금액↓ → 확정 금액↓ → 병의원 ID
 */
export interface PerformanceClinic {
  clinicId: number;
  clinicName: string;
  clinicTypeLabel: string | null;
  clinicTypeCodeName: string | null;
  address: string | null;
  /** 본인 거래처 ID — 처방 등록 API 경로. 없으면 null(등록 버튼 비활성) */
  relationshipId: number | null;
  /** 검수 중 추정 포함 */
  estimatedAmount: number;
  pendingEstimatedAmount: number;
  pendingImageCount: number;
  /** 그 정산월 노출 이미지 장수 */
  imageCount: number;
  confirmedAmount: number;
  hasSettlement: boolean;
  /**
   * TODO(server): 병의원별 정산 대상 금액 · 정산 비율 — 아직 응답에 없다 (M-08 선택 월 표 · 「정산 비율 낮은 곳」).
   * 없으면 화면이 병의원별 월 요약(/summary?clinicId=)으로 채운다. 목업만 내려준다.
   */
  targetAmountSettlement?: number;
  settlementRatio?: number | null;
}

export interface TrendComparison {
  amount: number;
  /** 직전 5개월 중 데이터 있는 첫 달부터 직전 달까지 평균(내림). 없으면 null */
  previous5MonthsAverage: number | null;
  /** % — 평균 0 · 없음이면 금액이 있을 때 100 */
  previous5MonthsChangeRate: number | null;
  previousMonthAmount: number | null;
  previousMonthChangeRate: number | null;
}

/** GET /monthly/{month}/clinics/{clinicId}/trend */
export interface ClinicTrend {
  settlementYearMonth: string;
  clinicId: number;
  year: number;
  /** 그 해 처방 또는 확정 정산이 있는 달, 오름차순 (차트 X축) */
  months: Array<{
    settlementYearMonth: string;
    estimatedAmount: number;
    confirmedAmount: number;
  }>;
  /** 예상 금액(승인된 확정 처방) 비교 */
  prescription: TrendComparison;
  settlement: TrendComparison;
  hasSettlement: boolean;
}

/** 처방 카드 승인 상태 코드 */
export type ApprovalStatusCode =
  | 'APPROVED'
  | 'UNREGISTERED_BUSINESS'
  | 'NON_TRADABLE_DRUG'
  | 'BELOW_MINIMUM_AMOUNT';

/**
 * 처방 카드 — 의약품 카드(6) · 이미지 연결 실적(9)이 같은 모양이다.
 * 정산 값은 같은 (처방월, 의약품) 정산을 합친 값이라 같은 약의 다른 카드에 반복될 수 있다.
 * 카드 금액을 더해 합계를 만들지 말고 제약사 칩 API의 합계를 쓴다.
 */
export interface PrescriptionCard {
  /** 카드 고유 키 */
  prescriptionId: number;
  prescriptionImageCode: string | null;
  prescriptionYearMonth: string;
  /** RU 코드는 "-" */
  drugCode: string;
  /** 스냅샷이 없으면 null → "—" */
  drugName: string | null;
  pharmaCompanyId: number | null;
  pharmaCompanyName: string | null;
  isReferenceDrug: boolean | null;
  isBioequivalent: boolean | null;
  reimbursementType: string | null;
  isOutOfStock: boolean | null;
  unitPrice: number | null;
  outHospitalQuantity: number | null;
  outHospitalAmount: number | null;
  /** % */
  feeRate: number | null;
  estimatedAmount: number | null;
  approvalStatusCode: ApprovalStatusCode | string | null;
  approvalStatus: string | null;
  /** 정산이 없으면 null */
  settlementQuantity: number | null;
  settlementAmount: number | null;
  /** 확정 상태 정산이 없으면 null (0과 구분) */
  confirmedAmount: number | null;
  /**
   * 정산 행이 여러 개면 정산불가 EDI > 미등록 거래처 > 거래 불가 > 최소금액 미달 > 흡수율 미달 > 승인 순.
   * 반려 표시와 양수 확정 금액이 함께 있을 수 있다.
   */
  settlementStatusCode: string | null;
  settlementStatus: string | null;
  remarks: string | null;
}

export const PRESCRIPTION_SORT_KEYS = [
  'CONFIRMED_AMOUNT_DESC',
  'CONFIRMED_AMOUNT_ASC',
  'ESTIMATED_AMOUNT_DESC',
  'ESTIMATED_AMOUNT_ASC',
  'OUT_HOSPITAL_QUANTITY_DESC',
  'COMMISSION_RATE_DESC',
] as const;
export type PrescriptionSortKey = (typeof PRESCRIPTION_SORT_KEYS)[number];

export interface ClinicDrugsParams {
  drugName?: string;
  pharmaCompanyIds?: number[];
  /** 여러 개. 생략하면 처방월 최신 → 예상 금액 */
  sortKeys?: PrescriptionSortKey[];
  page: number;
  size: number;
}

/** 제약사 칩 (M-17) — 정산에 제약사가 없으면 pharmaCompanyId · pharmaCompanyName이 null */
export interface PharmaCompanyChip {
  pharmaCompanyId: number | null;
  pharmaCompanyName: string | null;
  minPrescriptionAmount: number | null;
  /** 정산만 있는 제약사는 0 (카드 없음) */
  drugCount: number;
  estimatedAmount: number;
  targetAmount: number;
  confirmedAmount: number;
}

/** GET /monthly/{month}/clinics/{clinicId}/pharma-companies — 합계 = '전체' 칩 */
export interface ClinicPharmaCompanies {
  drugCount: number;
  estimatedAmount: number;
  targetAmount: number;
  confirmedAmount: number;
  /** 확정 금액↓ → 예상 금액↓ → 제약사 ID */
  pharmaCompanies: PharmaCompanyChip[];
}

/** 이미지 검수 상태 — VERIFIED만 확인 완료 */
export type ImageReviewStatus =
  'MODIFIED' | 'VERIFIED' | 'UNVERIFIED' | 'REJECTED';

/** GET /monthly/{month}/clinics/{clinicId}/images — 최신 업로드순 */
export interface PrescriptionImage {
  imageCode: string;
  /** 원본 파일명이 저장되지 않아 이미지 코드가 온다 */
  displayName: string;
  /** 만료되면 download-url로 다시 받는다. 원본이 없으면 null */
  imageUrl: string | null;
  createdAt: string;
  registrationDate: string;
  prescriptionYearMonth: string;
  registrationYearMonth: string;
  settlementYearMonth: string;
  reviewStatusCode: ImageReviewStatus | string;
  reviewStatus: string;
  /** 같은 사용자 · 병의원 · 정산월의 연결 확정 처방이 있음 — 검수 상태와 다른 축 */
  isConfirmed: boolean;
  /**
   * TODO(server): 처방 제외(REJECTED) 사유 (M-16 「처방 제외」 아래 빨간 글) — 아직 응답에 없다. 목업만 내려준다.
   */
  rejectReason?: string | null;
}

/** GET /monthly/{month}/clinics/{clinicId}/excel — S3 presigned URL(5분)로 받는다 */
export interface ClinicExcelExport {
  /** `{병의원명}_{정산년월}.xlsx` */
  fileName: string;
  downloadUrl: string;
  /** yyyy-MM-dd HH:mm:ss */
  expiresAt: string;
  rowCount: number;
}

const path = createApiPath.performance;

/**
 * 배열 쿼리는 같은 키를 반복한다 (pharmaCompanyIds=20&pharmaCompanyIds=21).
 * axios 기본 직렬화(key[]=)는 Spring이 받지 못한다.
 */
const repeatParams = (params: Record<string, unknown>) => {
  const search = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') return;
    (Array.isArray(value) ? value : [value]).forEach((v) =>
      search.append(key, String(v)),
    );
  });
  return search;
};

export const performanceApi = {
  availableYears: async () => {
    const { data } = await apiClient.get<number[]>(path.availableYears());
    return data;
  },
  yearly: async (year?: number, clinicId?: number) => {
    const { data } = await apiClient.get<YearlyPerformance>(path.yearly(), {
      params: { year, clinicId },
    });
    return data;
  },
  /** clinicId를 주면 그 병의원만, 없으면 본인 전체 */
  monthlySummary: async (month: string, clinicId?: number) => {
    const { data } = await apiClient.get<MonthlySummary>(
      path.monthlySummary(month),
      { params: { clinicId } },
    );
    return data;
  },
  clinics: async (
    month: string,
    params: { clinicName?: string; page: number; size: number },
  ) => {
    const { data } = await apiClient.get<PageResponse<PerformanceClinic>>(
      path.clinics(month),
      { params: { ...params, clinicName: params.clinicName || undefined } },
    );
    return data;
  },
  clinicTrend: async (month: string, clinicId: number, year?: number) => {
    const { data } = await apiClient.get<ClinicTrend>(
      path.clinicTrend(month, clinicId),
      { params: { year } },
    );
    return data;
  },
  clinicDrugs: async (
    month: string,
    clinicId: number,
    params: ClinicDrugsParams,
  ) => {
    const { data } = await apiClient.get<PageResponse<PrescriptionCard>>(
      path.clinicDrugs(month, clinicId),
      { params: repeatParams({ ...params }) },
    );
    return data;
  },
  clinicPharmaCompanies: async (month: string, clinicId: number) => {
    const { data } = await apiClient.get<ClinicPharmaCompanies>(
      path.clinicPharmaCompanies(month, clinicId),
    );
    return data;
  },
  clinicImages: async (month: string, clinicId: number) => {
    const { data } = await apiClient.get<PrescriptionImage[]>(
      path.clinicImages(month, clinicId),
    );
    return data;
  },
  /** 뷰어는 품목 수와 상관없이 전부 보여준다(M-18) — 한 번에 넉넉히 받는다 */
  imageDrugs: async (month: string, clinicId: number, imageCode: string) => {
    const { data } = await apiClient.get<PageResponse<PrescriptionCard>>(
      path.imageDrugs(month, clinicId, imageCode),
      { params: { page: 0, size: 100 } },
    );
    return data;
  },
  /** 404 PRESCRIPTION_IMAGE_NOT_FOUND · PRESCRIPTION_IMAGE_FILE_NOT_FOUND */
  imageDownloadUrl: async (
    month: string,
    clinicId: number,
    imageCode: string,
  ) => {
    const { data } = await apiClient.get<{
      imageCode: string;
      imageUrl: string;
    }>(path.imageDownloadUrl(month, clinicId, imageCode));
    return data;
  },
  /**
   * 병의원 하나 · 정산월의 의약품 카드 전체 엑셀 (필터 · 정렬 파라미터 없음, 행은 사용자 이메일순)
   * 404 WEB_PERFORMANCE_EXPORT_EMPTY · 500 WEB_PERFORMANCE_EXPORT_FAILED
   */
  clinicExcel: async (month: string, clinicId: number) => {
    const { data } = await apiClient.get<ClinicExcelExport>(
      path.clinicExcel(month, clinicId),
    );
    return data;
  },
};
